import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import PatientProcedureHistory from "./PatientProcedureHistory";
import "./Procedure.css";
import { API_BASE } from "../apiConfig";

function actorName() {
  return (
    localStorage.getItem("userName") ||
    localStorage.getItem("username") ||
    localStorage.getItem("userRole") ||
    "User"
  );
}

function fmtDate(value) {
  if (!value) return "-";
  const d = new Date(`${value}T00:00:00+05:30`);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric"
  }).format(d);
}

async function parseApi(response) {
  const text = await response.text();
  if (!text.trim()) throw new Error(`Empty response. HTTP ${response.status}`);
  const result = JSON.parse(text);
  if (!response.ok || result.status !== "success") {
    throw new Error(result.msg || "Request failed.");
  }
  return result;
}

export default function Procedure() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const role = String(localStorage.getItem("userRole") || "").trim().toLowerCase();
  const allowed = ["doctor", "reception", "nursing station", "nursing"].includes(role);

  const load = useCallback(async () => {
    if (!allowed) return;
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE}get_active_procedure_patients.php?q=${encodeURIComponent(q.trim())}`
      );
      const result = await parseApi(response);
      setRows(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      Swal.fire({ icon: "error", title: "Unable to Load Procedures", text: error.message });
    } finally {
      setLoading(false);
    }
  }, [q, allowed]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const cancelProcedure = async (row) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Cancel Procedure?",
      html: `<strong>${row.name}</strong><br>${row.procedure_details}`,
      input: "textarea",
      inputLabel: "Reason for cancellation",
      inputPlaceholder: "Enter reason...",
      showCancelButton: true,
      confirmButtonText: "Cancel Procedure",
      confirmButtonColor: "#d33",
      preConfirm: (value) => {
        if (!String(value || "").trim()) {
          Swal.showValidationMessage("Reason is required.");
          return false;
        }
        return String(value).trim();
      }
    });
    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`${API_BASE}cancel_procedure.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          procedure_id: Number(row.id),
          reason: result.value,
          cancelled_by: actorName()
        })
      });
      await parseApi(response);
      await load();
      Swal.fire({
        icon: "success",
        title: "Procedure Cancelled",
        timer: 1000,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Unable to Cancel", text: error.message });
    }
  };

  if (!allowed) {
    return (
      <div className="procedure-page">
        <div className="procedure-access-denied">
          <i className="fa fa-lock"></i>
          <h3>Access Restricted</h3>
          <p>Procedure page is available to Doctor, Reception and Nursing Station users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="procedure-page">
      <div className="procedure-page-header">
        <div>
          <h2 style={{fontSize:'18pt'}}><i className="fa fa-medkit"></i> Procedure</h2>
          <p>Patients who have active injection, dressing or other follow-up procedures.</p>
        </div>
        <div className="procedure-count-box">
          <strong>{rows.length}</strong>
          <span>Active Plans</span>
        </div>
      </div>

      <div className="procedure-search-card">
        <i className="fa fa-search"></i>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by MR ID, patient name, phone or procedure..."
        />
      </div>

      <div className="procedure-table-wrap">
        <table className="procedure-table">
          <thead>
            <tr>
              <th>#</th>
              <th>MR ID</th>
              <th>Patient</th>
              <th>Procedure</th>
              <th>Next Date</th>
              <th>Progress</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="procedure-table-message">
                <i className="fa fa-spinner fa-spin"></i> Loading...
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="7" className="procedure-table-message">
                No active procedures found.
              </td></tr>
            ) : (
              rows.map((row, index) => (
                <tr key={row.id}>
                  <td>{index + 1}</td>
                  <td><strong>{row.mr_no || "-"}</strong></td>
                  <td>
                    <strong>{row.name}</strong>
                    <small>{row.phone || "-"} • {row.age || "-"} / {row.sex || "-"}</small>
                  </td>
                  <td>
                    <div className="procedure-list-details">{row.procedure_details}</div>
                    <small>Started: {fmtDate(row.start_date)}</small>
                  </td>
                  <td>
                    <span className="procedure-next-date">{fmtDate(row.next_date)}</span>
                  </td>
                  <td>
                    <strong>{row.completed_count || 0}/{row.total_days}</strong>
                    <small>{row.pending_count || 0} pending</small>
                  </td>
                  <td>
                    <div className="procedure-table-actions">
                      <button
                        type="button"
                        className="procedure-btn view"
                        onClick={() => setSelected(row)}
                      >
                        <i className="fa fa-eye"></i> View Procedure
                      </button>
                      <button
                        type="button"
                        className="procedure-btn danger"
                        onClick={() => cancelProcedure(row)}
                      >
                        <i className="fa fa-ban"></i> Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="procedure-modal-overlay">
          <div className="procedure-modal">
            <div className="procedure-modal-header">
              <div>
                <h3 style={{color:'white'}}>{selected.name}</h3>
                <span style={{color:'white'}}>
                  MR ID: {selected.mr_no || "-"} &nbsp; | &nbsp; Phone: {selected.phone || "-"}
                </span>
              </div>
              <button type="button" onClick={() => setSelected(null)}>
                <i className="fa fa-times"></i>
              </button>
            </div>

            <div className="procedure-modal-body">
              <PatientProcedureHistory
                patientId={selected.patient_id}
                focusProcedureId={selected.id}
                canEdit={role === "doctor"}
                canComplete={true}
                onChanged={load}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
